/**
 * PayrollRunStepper — 5-lépcsős horizontális workflow vizualizáció
 * DRAFT → CALCULATED → BOLT_APPROVED → CENTRAL_REVIEWED → FINALIZED
 */

import { useTranslation } from '@kgc/i18n';
import type { PayrollRunStatus } from '../api/payroll-api';

interface Step {
  key: PayrollRunStatus;
  labelKey: string;
}

const STEPS: Step[] = [
  { key: 'DRAFT', labelKey: 'payroll.steps.draft' },
  { key: 'CALCULATED', labelKey: 'payroll.steps.calculated' },
  { key: 'BOLT_APPROVED', labelKey: 'payroll.steps.boltApproved' },
  { key: 'CENTRAL_REVIEWED', labelKey: 'payroll.steps.centralReviewed' },
  { key: 'FINALIZED', labelKey: 'payroll.steps.finalized' },
];

const STATUS_ORDER: Record<PayrollRunStatus, number> = {
  DRAFT: 0,
  CALCULATED: 1,
  BOLT_APPROVED: 2,
  CENTRAL_REVIEWED: 3,
  FINALIZED: 4,
};

interface PayrollRunStepperProps {
  currentStatus: PayrollRunStatus;
}

export function PayrollRunStepper({ currentStatus }: PayrollRunStepperProps) {
  const { t } = useTranslation();
  const currentOrder = STATUS_ORDER[currentStatus];

  return (
    <div className="flex items-start gap-0 w-full overflow-x-auto">
      {STEPS.map((step, index) => {
        const stepOrder = STATUS_ORDER[step.key];
        const isActive = stepOrder === currentOrder;
        const isPast = stepOrder < currentOrder;
        const isFuture = stepOrder > currentOrder;

        return (
          <div key={step.key} className="flex items-center flex-1 min-w-0">
            {/* Step circle + label */}
            <div className="flex flex-col items-center flex-1 min-w-0">
              <div
                className={[
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0',
                  isActive
                    ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2'
                    : isPast
                      ? 'bg-primary text-primary-foreground'
                      : isFuture
                        ? 'bg-muted text-muted-foreground'
                        : '',
                ].join(' ')}
              >
                {index + 1}
              </div>
              <span
                className={[
                  'mt-1 text-xs text-center leading-tight px-1',
                  isActive ? 'font-semibold text-foreground' : '',
                  isPast ? 'text-foreground' : '',
                  isFuture ? 'text-muted-foreground' : '',
                ].join(' ')}
              >
                {t(step.labelKey)}
              </span>
            </div>

            {/* Connector line (not after last step) */}
            {index < STEPS.length - 1 && (
              <div
                className={[
                  'h-0.5 flex-1 shrink-0 mx-1 mt-[-16px]',
                  isPast || isActive ? 'bg-primary' : 'bg-muted',
                ].join(' ')}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
